import {
  Module,
  VuexModule,
  Action,
  Mutation,
  config
} from "vuex-module-decorators";

import { documentStore, configStore, globalStore } from "@/store/store";
import { DEFAULT_MINIMUM_BREAKPOINT_WIDTH } from "@/lib/breakpoints/breakpoints";
import { SelectorCSSName } from "@/types/selectors";
import {
  Clipboard,
  CopiedControl,
  CopiedElementDesignAndSettings
} from "@/types/copy-paste";
import defensiveCopy from "@/util/defensive-copy";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { Breakpoint } from "@/types/breakpoints";
import { findNode } from "@/lib/tree/tree";
import { Control } from "@/types/controls";
import { BreakdanceNotification } from "@/types/notifications";
import { TreeNode } from "@/types/tree";
import { StructurePanelNodesExpandedState } from "@/lib/structure-panel/expand-collapse";
import {
  CanvasDragPlaceholderPosition,
  CanvasPreviewWidth,
  InterfaceState,
  PanelState,
  UIPanel,
  UnrecoverableErrorMessage,
  CanvasState,
  ElementsThatAreUpdatingMap,
  ClosingConfirmations,
  ClosingConfirmationDefinition,
  ClosingConfirmationChosenOption,
  ClosingConfirmationItem,
  LastModifiedElementProperty,
  UISettings,
  ClosingConfirmationSubject,
  UIPanelWhichLoadFreshDataOnceOpened
} from "@/types/ui";
import { RightClickMenuState } from "@/types/right-click";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import Vue from "vue";
import { scrollToActiveElement } from "@/lib/canvas/scroll-to-active-element";
import VueSimpleDeepset from "@/util/vue-simple-deepset";
import { getUiSettingFromLocalStorageOrDefaults } from "@/lib/ui-preference/ui-preference";
import { DocumentRevisionsData } from "@/types/revisions";

config.rawError = true;
@Module({ name: "ui", namespaced: true })
export default class UIModule extends VuexModule {
  mode: null | "builder" | "browse" = null;
  canvasDragPlaceholderPosition: null | CanvasDragPlaceholderPosition = null;
  activeSelectorName: null | SelectorCSSName = ""; // ".blue-section";
  activeElementId: null | number = null;
  builderBehaviorElementId: null | number = null;
  elementWithContentEditable: null | number = null;
  lastHoveredOnCanvasElementId: null | number = null;
  currentBreakpoint: Breakpoint = {
    id: BASE_BREAKPOINT_ID,
    label: "",
    minWidth: undefined,
    maxWidth: undefined,
    defaultPreviewWidth: DEFAULT_MINIMUM_BREAKPOINT_WIDTH
  };
  canvasPreviewWidth: CanvasPreviewWidth = {
    width: "100%",
    availableCanvasSize: {
      width: 0,
      height: 0
    }
  };
  currentlyDraggingCanvasElementId: null | number = null;
  // Element being added from the 'Add' panel
  currentlyDraggingAddElementSlug: null | string = null;
  isDragMixinDragging = false;
  interfaceState: InterfaceState = "loading";
  canvasState: CanvasState = "loading";
  unrecoverableErrorMessages: UnrecoverableErrorMessage[] = [];
  unsavedChangesPresent = false;
  clipboard: Clipboard = {
    copiedControlData: false,
    copiedElementDesignAndSettings: false,
    elementStudio: false
  };
  consoleErrorsPresent = false;

  onCanvasRightClickMenuState: RightClickMenuState = "closed";
  onStructurePanelRightClickMenuState: RightClickMenuState = "closed";

  panelState: PanelState = {
    leftSidebarPanel: "closed",
    rightSidebarPanel: "closed",
    preferencesModalOpen: false,
    keyboardShortcutsHelpModalOpen: false,
    designLibraryModalOpen: false,
    elementStudioOpen: false,
    accordionState: {
      elementProperties: {},
      selectors: {},
      globalSettings: {},
      dynamicDataFieldAttributes: {}
    },
    elementPropertiesPanelActiveTab: {},
    shouldLoadFreshDataIntoStoreOnceOpened: {
      globalSettings: true,
      selectors: true
    },
    globalSettingsPanelActiveViewMode: "controls",
    selectorsPanelPanelActiveViewMode: "controls"
  };

  closingConfirmations: ClosingConfirmations = {};

  readOnly = false;

  notifications: BreakdanceNotification[] = [];

  isLoggedOut = false;

  elementsThatAreUpdating: ElementsThatAreUpdatingMap = {};

  // todo - get this out of the UI store
  idsOfPostsToRegenerateCache: null | number[] = null;

  lastModifiedElementProperty: LastModifiedElementProperty = {
    elementId: null,
    path: null
  };

  refreshSsrElementId: number | null = null;

  settings: UISettings = getUiSettingFromLocalStorageOrDefaults();

  /**
   * Nodes, that are:
   * – not present at this object,
   * – mapped to "undefined" value,
   * – mapped to "true" value,
   * are treated as expanded.
   */
  structurePanelNodesExpandedState: StructurePanelNodesExpandedState = {};

  revisionsData: DocumentRevisionsData = {
    revisions: [],
    currentUnsavedRevision: null
  };

  canvasKey = 1;

  // BEWARE of using this action. It has huge performance penalty
  @Action
  forceUpdateEntireCanvas() {
    this.setCanvasKey(this.canvasKey + 1);
  }

  @Mutation
  setCanvasKey(newCanvasKey: number) {
    this.canvasKey = newCanvasKey;
  }

  @Mutation
  setMode(newMode: "builder" | "browse") {
    this.mode = newMode;
  }

  @Mutation
  setOnCanvasRightClickMenuState(
    newOnCanvasRightClickMenuState: RightClickMenuState
  ) {
    this.onCanvasRightClickMenuState = newOnCanvasRightClickMenuState;
  }

  @Mutation
  setOnStructurePanelRightClickMenuState(menuState: RightClickMenuState) {
    this.onStructurePanelRightClickMenuState = menuState;
  }

  @Mutation
  setNotifications(newNotifications: BreakdanceNotification[]) {
    this.notifications = newNotifications;
  }

  @Mutation
  copyElementStudioControl(copiedControl: Control) {
    this.clipboard.elementStudio = {
      ...this.clipboard.elementStudio,
      copiedControl: copiedControl
    };
  }

  @Mutation
  copyControlValueToClipboard(copiedControl: CopiedControl) {
    this.clipboard = {
      ...this.clipboard,
      copiedControlData: copiedControl
    };
  }

  @Mutation
  copyElementDesignAndSettings(
    copiedElementDesignAndSettings: CopiedElementDesignAndSettings
  ) {
    this.clipboard = {
      ...this.clipboard,
      copiedElementDesignAndSettings: copiedElementDesignAndSettings
    };
  }

  @Mutation
  setUnsavedChangesPresent(unsavedChangesPresent: boolean) {
    this.unsavedChangesPresent = unsavedChangesPresent;
  }

  @Mutation
  setPanelState(panelState: PanelState) {
    this.panelState = panelState;
  }

  @Action
  logConsoleError(error: unknown) {
    console.log(error);
    this.setConsoleErrorsPresentFlag(true);
  }

  @Mutation
  setConsoleErrorsPresentFlag(present: boolean) {
    this.consoleErrorsPresent = present;
  }

  // @Action
  // logRecoverableError(newErrorMessage: string) {
  //   NotificationLogger.log.message(newErrorMessage);
  //   // const indexOfError = this.recoverableErrorOrNotices.findIndex(
  //   //   ({ error }) => error === newErrorMessage
  //   // );

  //   // if (indexOfError === -1) {
  //   //   this.recoverableErrorOrNotices.push({
  //   //     count: 1,
  //   //     error: newErrorMessage
  //   //   });
  //   // } else {
  //   //   this.recoverableErrorOrNotices[indexOfError].count++;
  //   // }
  // }

  @Action
  setLeftSidebarState(sidebarState: UIPanel | "closed") {
    const newPanelState = defensiveCopy(this.panelState);
    return this.setPanelState({
      ...newPanelState,
      leftSidebarPanel: sidebarState
    });
  }

  @Action
  setRightSidebarState(sidebarState: UIPanel | "closed") {
    const newPanelState = defensiveCopy(this.panelState);
    return this.setPanelState({
      ...newPanelState,
      rightSidebarPanel: sidebarState
    });
  }

  @Action
  setDesignLibraryModalState(designLibraryModalOpen: boolean) {
    const newPanelState = defensiveCopy(this.panelState);

    return this.setPanelState({
      ...newPanelState,
      designLibraryModalOpen
    });
  }

  @Mutation
  setPreferencesModalOpen(open: boolean) {
    this.panelState.preferencesModalOpen = open;
  }

  @Mutation
  setKeyboardShortcutHelpModalOpen(open: boolean) {
    this.panelState.keyboardShortcutsHelpModalOpen = open;
  }

  @Mutation
  setShouldPanelLoadFreshDataIntoStoreOnceOpened(
    panel: UIPanelWhichLoadFreshDataOnceOpened,
    should: boolean
  ) {
    this.panelState.shouldLoadFreshDataIntoStoreOnceOpened[panel] = should;
  }

  @Mutation
  toggleElementStudio() {
    this.panelState.elementStudioOpen = !this.panelState.elementStudioOpen;
  }

  @Mutation
  setInterfaceState(state: InterfaceState) {
    this.interfaceState = state;
  }

  @Mutation
  setCanvasState(state: CanvasState) {
    this.canvasState = state;
  }

  @Mutation
  logError(msg: UnrecoverableErrorMessage) {
    this.unrecoverableErrorMessages.push(msg);
  }

  @Mutation
  setActiveSelectorName(selectorName: SelectorCSSName | null) {
    this.activeSelectorName = selectorName;
  }

  @Mutation
  setBuilderBehaviorElementId(id: null | number) {
    this.builderBehaviorElementId = id;
  }

  @Action
  activateElement(elementId: number | null) {
    const canDisableBuilderBehavior =
      this.builderBehaviorElementId &&
      elementId !== this.builderBehaviorElementId;

    if (canDisableBuilderBehavior) {
      this.setBuilderBehaviorElementId(null);
    }

    if (
      elementId === documentStore.document.tree.root.id ||
      elementId === null
    ) {
      this.activateElementMutation(null);
    } else {
      this.activateElementMutation(elementId);

      breakdanceStoreActions.run.onActivatedElement({ elementId });
      scrollToActiveElement();
    }
  }

  @Action
  setContentEditableElementId(elementId: number | null) {
    this.contentEditableElementIdMutation(elementId);
  }

  @Action
  selectNextBreakpoint() {
    const currentBreakpointIndex = configStore.breakpoints.indexOf(
      this.currentBreakpoint
    );

    if (currentBreakpointIndex === -1) return;

    const nextBreakpointIndex =
      currentBreakpointIndex + 1 < configStore.breakpoints.length
        ? currentBreakpointIndex + 1
        : 0;

    this.changeBreakpoint(configStore.breakpoints[nextBreakpointIndex]);
  }

  @Action
  changeBreakpoint(newBreakpoint: Breakpoint) {
    if (newBreakpoint.id === BASE_BREAKPOINT_ID) {
      this.selectBaseBreakpoint();
      return;
    }

    this.changeBreakpointMutation(newBreakpoint);

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width: newBreakpoint.defaultPreviewWidth
    });
  }

  @Action
  selectBaseBreakpoint() {
    const baseBreakpoint = configStore.breakpoints.find(
      breakpoint => breakpoint.id === BASE_BREAKPOINT_ID
    );

    if (!baseBreakpoint) return;

    this.changeBreakpointMutation(baseBreakpoint);

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width: "100%"
    });
  }

  @Action
  changePreviewWidthWithAdjustmentBar(previewWidthInPx: number) {
    const breakpoint = [...configStore.builtinBreakpoints]
      .reverse()
      .find(b => b.maxWidth && b.maxWidth >= previewWidthInPx);

    if (breakpoint) {
      if (breakpoint.id !== this.currentBreakpoint.id) {
        this.changeBreakpointMutation(breakpoint);
      }
    } else {
      const baseBreakpoint = configStore.builtinBreakpoints.find(
        b => b.id === BASE_BREAKPOINT_ID
      );

      if (baseBreakpoint && baseBreakpoint.id !== this.currentBreakpoint.id) {
        this.changeBreakpointMutation(baseBreakpoint);
      }
    }

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width:
        DEFAULT_MINIMUM_BREAKPOINT_WIDTH > previewWidthInPx
          ? DEFAULT_MINIMUM_BREAKPOINT_WIDTH
          : previewWidthInPx
    });
  }

  @Mutation
  setCanvasDragPlaceholderPosition(
    canvasDragPlaceholderPosition: CanvasDragPlaceholderPosition | null
  ) {
    this.canvasDragPlaceholderPosition = canvasDragPlaceholderPosition;
  }

  @Mutation
  activateElementMutation(elementId: number | null) {
    this.activeElementId = elementId;
  }

  @Mutation
  contentEditableElementIdMutation(elementId: number | null) {
    this.elementWithContentEditable = elementId;
  }

  @Mutation
  setLastHoveredOnCanvasElementId(elementId: number | null) {
    this.lastHoveredOnCanvasElementId = elementId;
  }

  @Mutation
  changeBreakpointMutation(newBreakpoint: Breakpoint) {
    this.currentBreakpoint = newBreakpoint;
  }

  @Mutation
  changePreviewWidthMutation(previewWidth: CanvasPreviewWidth) {
    this.canvasPreviewWidth = previewWidth;
  }

  @Mutation
  setAddElementDraggedSlug(draggedElementSlug: string | null) {
    this.currentlyDraggingAddElementSlug = draggedElementSlug;
  }

  @Mutation
  setCanvasElementDraggedId(draggedElementId: number | null) {
    this.currentlyDraggingCanvasElementId = draggedElementId;
  }

  @Mutation
  endCanvasElementDrag() {
    this.currentlyDraggingCanvasElementId = null;
  }

  @Mutation
  setDragMixinDragging(isDragMixinDragging: boolean) {
    this.isDragMixinDragging = isDragMixinDragging;
  }

  @Mutation
  setStructurePanelNodesExpandedState(
    newStructurePanelNodesExpandedState: StructurePanelNodesExpandedState
  ) {
    this.structurePanelNodesExpandedState = defensiveCopy(
      newStructurePanelNodesExpandedState
    );
  }

  get activeElement(): false | TreeNode {
    if (this.activeElementId === null) return false;

    const foundNode = findNode(
      documentStore.document.tree,
      this.activeElementId
    );
    return foundNode || false;
  }

  get builderBehaviorActiveElement(): false | TreeNode {
    if (this.builderBehaviorElementId === null) return false;

    const foundNode = findNode(
      documentStore.document.tree,
      this.builderBehaviorElementId
    );

    return foundNode || false;
  }

  get activeSelector() {
    if (!this.activeSelectorName) return false;

    const foundSelector = globalStore.cssSelectors.find(
      selector => selector.name === this.activeSelectorName
    );

    return foundSelector ? foundSelector : false;
  }

  get isCanvasElementBeingDragged() {
    return this.currentlyDraggingCanvasElementId !== null;
  }

  get isAddElementBeingDragged() {
    return this.currentlyDraggingAddElementSlug !== null;
  }

  get hasFullAccess() {
    return configStore.permission?.slug === "full";
  }

  @Mutation
  setLoggedOut(isLoggedOut: boolean) {
    this.isLoggedOut = isLoggedOut;
  }

  @Mutation
  setElementUpdatingState(newElementUpdateState: {
    elementId: number;
    isUpdating: boolean;
  }) {
    Vue.set(
      this.elementsThatAreUpdating,
      newElementUpdateState.elementId,
      newElementUpdateState.isUpdating
    );
  }

  @Mutation
  setIdsOfPostsToRegenerateCache(maybePostIds: number[] | null) {
    this.idsOfPostsToRegenerateCache = maybePostIds;
  }

  @Mutation
  requestClosingConfirmation({
    subject,
    confirmationDefinition
  }: {
    subject: ClosingConfirmationSubject;
    confirmationDefinition: ClosingConfirmationDefinition;
  }) {
    const confirmationItem: ClosingConfirmationItem = {
      ...confirmationDefinition,
      show: false,
      isSaving: false,
      isDiscarding: false
    };
    Vue.set(this.closingConfirmations, subject, confirmationItem);
  }

  @Mutation
  chooseOptionForClosingConfirmation({
    subject,
    chosenOption
  }: {
    subject: ClosingConfirmationSubject;
    chosenOption: ClosingConfirmationChosenOption;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.chosenOption`,
        chosenOption
      );
    }
  }

  @Mutation
  showClosingConfirmation(subject: ClosingConfirmationSubject) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(this.closingConfirmations, `${subject}.show`, true);
    }
  }

  @Mutation
  hideClosingConfirmation(subject: ClosingConfirmationSubject) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(this.closingConfirmations, `${subject}.show`, false);
    }
  }

  @Mutation
  noClosingConfirmationIsNeededFor(subject: ClosingConfirmationSubject) {
    VueSimpleDeepset(this.closingConfirmations, subject, undefined);
  }

  @Mutation
  setClosingConfirmationIsDiscarding({
    subject,
    isDiscarding
  }: {
    subject: ClosingConfirmationSubject;
    isDiscarding: boolean;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.isDiscarding`,
        isDiscarding
      );
    }
  }

  @Mutation
  setClosingConfirmationIsSaving({
    subject,
    isSaving
  }: {
    subject: ClosingConfirmationSubject;
    isSaving: boolean;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.isSaving`,
        isSaving
      );
    }
  }

  @Mutation
  setLastModifiedElementProperty({
    elementId,
    path
  }: LastModifiedElementProperty) {
    this.lastModifiedElementProperty = {
      elementId,
      path
    };
  }

  @Mutation
  setRefreshSsrElementId(elementId: number | null) {
    this.refreshSsrElementId = elementId;
  }

  @Mutation
  setSettings(newSettings: UISettings) {
    this.settings = newSettings;
  }

  @Mutation
  setRevisionsData(newRevisionsData: DocumentRevisionsData) {
    this.revisionsData = newRevisionsData;
  }
}
